import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import KPICard from "../../components/shared/KPICard";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { toast } from "sonner";
import {
  Users,
  Store,
  DollarSign,
  TrendingUp,
  MousePointer,
  Wallet,
  ArrowUpRight,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateDashboard() {
  const { getAuthHeaders } = useAuth();
  const { theme } = useTheme();
  const [overview, setOverview] = useState(null);
  const [dailyStats, setDailyStats] = useState({ earnings: [], clicks: [] });
  const [funnel, setFunnel] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { ...getAuthHeaders(), "Content-Type": "application/json" };
      
      const [overviewRes, statsRes, funnelRes, merchantsRes] = await Promise.all([
        fetch(`${API_URL}/api/affiliate/overview`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/affiliate/stats/daily`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/affiliate/funnel`, { credentials: "include", headers }),
        fetch(`${API_URL}/api/affiliate/merchants`, { credentials: "include", headers }),
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (statsRes.ok) setDailyStats(await statsRes.json());
      if (funnelRes.ok) setFunnel(await funnelRes.json());
      if (merchantsRes.ok) setMerchants(await merchantsRes.json());
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(value || 0);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat("en-US").format(value || 0);
  };

  const funnelData = funnel
    ? [
        { name: "Clicks", value: funnel.clicks, fill: "#3B82F6" },
        { name: "Signups", value: funnel.signups, fill: "#8B5CF6" },
        { name: "Active", value: funnel.active, fill: "#10B981" },
        { name: "Transacting", value: funnel.transacting, fill: "#F59E0B" },
      ]
    : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`rounded-lg p-3 shadow-lg border ${
          theme === 'dark' 
            ? 'bg-[#012169] border-white/10' 
            : 'bg-white border-slate-200'
        }`}>
          <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
              {entry.name}: {entry.name.includes("$") || entry.dataKey === "earnings" 
                ? formatCurrency(entry.value) 
                : formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout title="Dashboard" subtitle="Overview of your affiliate performance">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard" subtitle="Overview of your affiliate performance">
      <div data-testid="affiliate-dashboard" className="space-y-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
          <KPICard
            title="Total Merchants"
            value={formatNumber(overview?.merchants_referred)}
            subtitle={`${formatNumber(overview?.merchants_active)} active`}
            icon={Store}
            variant="blue"
          />
          <KPICard
            title="Total Clicks"
            value={formatNumber(overview?.total_clicks)}
            icon={MousePointer}
            variant="purple"
          />
          <KPICard
            title="Total Volume"
            value={formatCurrency(overview?.total_volume)}
            subtitle={`${formatCurrency(overview?.total_fees)} in fees`}
            icon={TrendingUp}
            variant="green"
          />
          <KPICard
            title="Total Earnings"
            value={formatCurrency(overview?.earnings?.total)}
            subtitle={`${formatCurrency(overview?.earnings?.payable)} available`}
            icon={Wallet}
            variant="amber"
          />
        </div>

        {/* Earnings breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Pending</p>
            <p className="text-2xl font-bold text-amber-500">{formatCurrency(overview?.earnings?.pending)}</p>
          </div>
          <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Payable</p>
            <p className="text-2xl font-bold text-emerald-500">{formatCurrency(overview?.earnings?.payable)}</p>
          </div>
          <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <p className={`text-sm mb-1 ${theme === 'dark' ? 'text-slate-400' : 'text-slate-500'}`}>Paid Out</p>
            <p className="text-2xl font-bold text-blue-500">{formatCurrency(overview?.earnings?.paid)}</p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Earnings Chart */}
          <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <h3 className={`text-lg font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
              Earnings Over Time
            </h3>
            <div className="h-64" style={{ minWidth: '100%' }}>
              {dailyStats.earnings.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={dailyStats.earnings}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#334155' : '#e2e8f0'} />
                    <XAxis dataKey="_id" stroke={theme === 'dark' ? '#94a3b8' : '#64748b'} tick={{ fontSize: 12 }} />
                    <YAxis stroke={theme === 'dark' ? '#94a3b8' : '#64748b'} tick={{ fontSize: 12 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Line
                      type="monotone"
                      dataKey="earnings"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      dot={{ fill: "#3B82F6", strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className={`h-full flex items-center justify-center ${
                  theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  No earnings data yet
                </div>
              )}
            </div>
          </div>

          {/* Conversion Funnel */}
          <div className={`rounded-xl p-6 border ${
            theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
          }`}>
            <h3 className={`text-lg font-semibold mb-6 ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
              Conversion Funnel
            </h3>
            <div className="h-64" style={{ minWidth: '100%' }}>
              {funnel ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={funnelData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke={theme === 'dark' ? '#334155' : '#e2e8f0'} horizontal={false} />
                    <XAxis type="number" stroke={theme === 'dark' ? '#94a3b8' : '#64748b'} tick={{ fontSize: 12 }} />
                    <YAxis type="category" dataKey="name" stroke={theme === 'dark' ? '#94a3b8' : '#64748b'} tick={{ fontSize: 12 }} width={80} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {funnelData.map((entry, index) => (
                        <Cell key={index} fill={entry.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className={`h-full flex items-center justify-center ${
                  theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
                }`}>
                  No funnel data yet
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Recent Merchants */}
        <div className={`rounded-xl overflow-hidden border ${
          theme === 'dark' ? 'bg-[#012169] border-white/5' : 'bg-white border-slate-200 shadow-sm'
        }`}>
          <div className={`p-6 border-b ${theme === 'dark' ? 'border-white/5' : 'border-slate-200'}`}>
            <h3 className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
              Recent Referred Merchants
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Merchant</th>
                  <th>Status</th>
                  <th>Country</th>
                  <th>Volume</th>
                  <th>Earnings</th>
                </tr>
              </thead>
              <tbody>
                {merchants.length > 0 ? (
                  merchants.slice(0, 5).map((merchant) => (
                    <tr key={merchant.merchant_id}>
                      <td>
                        <div>
                          <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-[#011a3a]'}`}>
                            {merchant.name}
                          </p>
                          <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-slate-400'}`}>
                            {merchant.website}
                          </p>
                        </div>
                      </td>
                      <td>
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            merchant.status === "active"
                              ? "status-active"
                              : merchant.status === "signed_up"
                              ? "status-pending"
                              : "bg-slate-500/20 text-slate-500"
                          }`}
                        >
                          {merchant.status}
                        </span>
                      </td>
                      <td className={theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}>
                        {merchant.country || "-"}
                      </td>
                      <td className={theme === 'dark' ? 'text-slate-300' : 'text-slate-600'}>
                        {formatCurrency(merchant.total_volume)}
                      </td>
                      <td className="text-emerald-500 font-medium">
                        {formatCurrency(merchant.total_earnings)}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className={`text-center py-8 ${
                      theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
                    }`}>
                      No merchants referred yet. Share your referral links to get started!
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
